import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useContract } from '../context/ContractContext';
import { usePurchase } from '../context/PurchaseContext';
import { useValidation } from '../context/ValidationContext';
import { getDownloadFilename } from '../utils/downloadFilename';
import { readGuestRentDraft, writeGuestRentDraft, clearGuestRentDraft } from '../utils/guestRentDraft';
import HeaderAndPartiesSection from './sections/HeaderAndPartiesSection';
import PropertySection from './sections/PropertySection';
import TermSection from './sections/TermSection';
import RightsAndObligationsSection from './sections/RightsAndObligationsSection';
import UsageConditionsSection from './sections/UsageConditionsSection';
import PaymentSection from './sections/PaymentSection';
import FinancialLiabilitySection from './sections/FinancialLiabilitySection';
import DisputeResolutionSection from './sections/DisputeResolutionSection';
import AttachmentsSection from './sections/AttachmentsSection';
import FinalProvisionsSection from './sections/FinalProvisionsSection';
import SignaturesSection from './sections/SignaturesSection';
import HandoverActSection from './sections/HandoverActSection';
import ReturnActSection from './sections/ReturnActSection';
import MoneyTransferActSection from './sections/MoneyTransferActSection';
import Sidebar from './common/Sidebar';
import LoadingSpinner from './common/LoadingSpinner';
import PaymentModal from './Payment/PaymentModal';
import InfoModal from './common/InfoModal';

const CURRENT_TEMPLATE_ID = 'rent_apartment';
const LAST_RENT_CONTRACT_SESSION_KEY = 'docgen_last_rent_contract_id';

/**
 * Основной компонент-конструктор договора.
 * Собирает все секции, управляет состоянием и взаимодействием с API.
 */
const ContractWizard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated } = useAuth();
    const { currentContract, contractId, isLoading, isSaving, error, initializeNewContract, restoreContractData, loadContract, updateContractData, saveContract, isNewContract } = useContract();
    const { getContractStatus, downloadContract, checkTemplatePurchase, purchaseChecks, getTemplateById } = usePurchase();
    const { showValidation, triggerValidation, resetValidation, scrollToFirstInvalidField } = useValidation();

    const [paymentModal, setPaymentModal] = useState({ isOpen: false, templateId: null, contractId: null });
    const [validationModal, setValidationModal] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [downloadingFormat, setDownloadingFormat] = useState(null);
    const isInitialMount = useRef(true);
    const saveContractRef = useRef(saveContract);
    const serverBootstrapRef = useRef(false);
    /** После navigate(/contract/rent) из ветки ?new=1 эффект отрабатывает ещё раз — без флага форма переинициализируется */
    const skipInitAfterNewRef = useRef(false);

    useEffect(() => {
        saveContractRef.current = saveContract;
    }, [saveContract]);

    // Сессия редактора: загрузка по id, черновик гостя, новый договор с привязкой к URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const contractIdFromUrl = params.get('contractId');
        const wantNew = params.get('new') === '1';

        if (contractIdFromUrl && isAuthenticated) {
            serverBootstrapRef.current = false;
            loadContract(contractIdFromUrl);
            return;
        }

        if (!isAuthenticated) {
            if (skipInitAfterNewRef.current) {
                skipInitAfterNewRef.current = false;
                return;
            }
            if (wantNew) {
                clearGuestRentDraft();
                initializeNewContract(CURRENT_TEMPLATE_ID);
                skipInitAfterNewRef.current = true;
                navigate('/contract/rent', { replace: true });
                return;
            }
            const draft = readGuestRentDraft();
            if (draft?.templateId === CURRENT_TEMPLATE_ID && draft.data) {
                restoreContractData(CURRENT_TEMPLATE_ID, draft.data);
            } else {
                initializeNewContract(CURRENT_TEMPLATE_ID);
            }
            return;
        }

        if (skipInitAfterNewRef.current) {
            skipInitAfterNewRef.current = false;
            return;
        }

        if (wantNew) {
            clearGuestRentDraft();
            initializeNewContract(CURRENT_TEMPLATE_ID);
            skipInitAfterNewRef.current = true;
            navigate('/contract/rent', { replace: true });
            return;
        }

        try {
            const lastId = sessionStorage.getItem(LAST_RENT_CONTRACT_SESSION_KEY);
            if (lastId) {
                navigate(`/contract/rent?contractId=${lastId}`, { replace: true });
                return;
            }
        } catch {
            /* ignore */
        }

        serverBootstrapRef.current = false;
        initializeNewContract(CURRENT_TEMPLATE_ID);
    }, [
        isAuthenticated,
        location.search,
        initializeNewContract,
        loadContract,
        restoreContractData,
        navigate
    ]);

    // Авторизован без contractId в URL: сразу создаём запись на сервере и подставляем id в адрес (F5 не плодит новый черновик)
    useEffect(() => {
        if (!isAuthenticated) return;
        const idInUrl = new URLSearchParams(location.search).get('contractId');
        if (idInUrl) return;
        if (!currentContract?.data) return;
        if (contractId) return;
        if (serverBootstrapRef.current) return;
        serverBootstrapRef.current = true;
        (async () => {
            try {
                const saved = await saveContractRef.current();
                if (saved?.id) {
                    navigate(`/contract/rent?contractId=${saved.id}`, { replace: true });
                } else {
                    serverBootstrapRef.current = false;
                }
            } catch {
                serverBootstrapRef.current = false;
            }
        })();
    }, [isAuthenticated, currentContract?.data, contractId, location.search, navigate]);

    // URL без contractId, в state уже есть id (сохранение/скачивание) — подставляем в адрес, иначе F5 создаст новый черновик
    useEffect(() => {
        if (!isAuthenticated || !contractId) return;
        const idInUrl = new URLSearchParams(location.search).get('contractId');
        if (idInUrl === contractId) return;
        if (idInUrl) return;
        navigate(`/contract/rent?contractId=${contractId}`, { replace: true });
    }, [isAuthenticated, contractId, location.search, navigate]);

    useEffect(() => {
        if (!isAuthenticated || !contractId) return;
        try {
            sessionStorage.setItem(LAST_RENT_CONTRACT_SESSION_KEY, contractId);
        } catch {
            /* ignore */
        }
    }, [isAuthenticated, contractId]);

    // Автосохранение: на сервере или в localStorage (гость)
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        if (!currentContract?.data) return;

        if (isAuthenticated) {
            const handler = setTimeout(() => {
                saveContractRef.current();
            }, 1500);
            return () => clearTimeout(handler);
        }

        const handler = setTimeout(() => {
            writeGuestRentDraft({
                templateId: CURRENT_TEMPLATE_ID,
                data: currentContract.data
            });
        }, 800);
        return () => clearTimeout(handler);
    }, [currentContract?.data, isAuthenticated]);

    const handleDownload = useCallback(async (format) => {
        // Yandex.Metrika: единая цель нажатия кнопки скачивания
        try {
            if (typeof window !== 'undefined' && typeof window.ym === 'function') {
                window.ym(107157002, 'reachGoal', 'rent_download');
            }
        } catch (e) {
            console.error('Yandex.Metrika error:', e);
        }

        // ✨ Валидация обязательных полей перед скачиванием
        const validationResult = triggerValidation(currentContract.data);
        
        if (!validationResult.isValid) {
            setValidationErrors(validationResult.errors || []);
            // Сначала прокрутка (пока body без modal-open), потом модалка — иначе position:fixed на body ломает scrollIntoView
            scrollToFirstInvalidField(validationResult.firstInvalidFieldId, () => {
                setValidationModal(true);
            });
            return;
        }

        // Сбрасываем валидацию если все ок
        resetValidation();

        if (!isAuthenticated) {
            const pendingData = {
                data: currentContract.data,
                trigger: 'download',
                templateId: CURRENT_TEMPLATE_ID,
                timestamp: Date.now()
            };
            localStorage.setItem('pending_contract_data', JSON.stringify(pendingData));
            navigate('/login');
            return;
        }

        let finalContractId = contractId;
        if (isNewContract()) {
            const savedContract = await saveContract();
            if (!savedContract?.id) {
                alert('Не удалось сохранить договор перед скачиванием.');
                return;
            }
            finalContractId = savedContract.id;
        }

        const purchaseCheck = purchaseChecks[CURRENT_TEMPLATE_ID] || await checkTemplatePurchase(CURRENT_TEMPLATE_ID);
        const contractForStatusCheck = { id: finalContractId, templateId: CURRENT_TEMPLATE_ID, isPurchased: purchaseCheck?.hasPurchase || false };
        const status = getContractStatus(contractForStatusCheck);

        if (status.canDownload) {
            try {
                setDownloadingFormat(format);
                const contractForFilename = { templateId: CURRENT_TEMPLATE_ID, data: currentContract.data };
                const suggestedFilename = getDownloadFilename(contractForFilename, format, getTemplateById);
                await downloadContract(finalContractId, format, suggestedFilename);
            } catch (e) {
                alert(`Ошибка скачивания: ${e.message}`);
            } finally {
                setDownloadingFormat(null);
            }
        } else {
            setPaymentModal({ isOpen: true, templateId: CURRENT_TEMPLATE_ID, contractId: finalContractId });
        }
    }, [isAuthenticated, currentContract, contractId, isNewContract, navigate, saveContract, checkTemplatePurchase, getContractStatus, downloadContract, getTemplateById, purchaseChecks, triggerValidation, resetValidation, scrollToFirstInvalidField]);

    if (isLoading || !currentContract) {
        return <LoadingSpinner message="Загрузка конструктора..." fullScreen />;
    }

    if (error) {
        return <div className="error-container"><h3>Ошибка:</h3><p>{error}</p></div>;
    }

    return (
        <>
            <div className="contract-wizard">
                <div className="section-header">
                    <h1>Конструктор договора</h1>
                </div>
                <div className="wizard-layout">
                    <main className="contract-form-area">
                        <div className="section-block">
                            <HeaderAndPartiesSection
                                data={currentContract.data}
                                onChange={updateContractData}
                                showValidation={showValidation}
                            />
                        </div>

                        <div className="section-block">
                            <PropertySection
                            data={currentContract.data.property}
                            onChange={(propData) => updateContractData({ ...currentContract.data, property: propData })}
                            showValidation={showValidation}
                            contractData={currentContract.data}
                            />
                        </div>

                        <div className="section-block">
                            <TermSection
                            data={currentContract.data.term}
                            onChange={(termData) => updateContractData({ ...currentContract.data, term: termData })}
                            />
                        </div>

                        <div className="section-block">
                            <RightsAndObligationsSection
                                data={currentContract.data.rightsAndObligations}
                                onChange={(newData) => updateContractData({ ...currentContract.data, rightsAndObligations: newData })}
                                contractData={currentContract.data}
                            />
                        </div>

                        <div className="section-block">
                            <PaymentSection
                                data={currentContract.data.payment}
                                onChange={(newData) => updateContractData({ ...currentContract.data, payment: newData })}
                                showValidation={showValidation}
                                contractData={currentContract.data}
                            />
                        </div>

                        <div className="section-block">
                            <UsageConditionsSection
                                data={currentContract.data.usageConditions}
                                onChange={(newData) => updateContractData({ ...currentContract.data, usageConditions: newData })}
                            />
                        </div>

                        <div className="section-block">
                            <FinancialLiabilitySection 
                                data={currentContract.data.financialLiability} 
                                onChange={(newData) => updateContractData({ ...currentContract.data, financialLiability: newData })} 
                            />
                        </div>

                        <div className="section-block">
                            <DisputeResolutionSection
                            data={currentContract.data.disputeResolution}
                            onChange={(newData) => updateContractData({ ...currentContract.data, disputeResolution: newData })}
                            />
                        </div>

                        <div className="section-block">
                            <FinalProvisionsSection
                                data={currentContract.data}
                            />
                        </div>

                        <div className="section-block">
                            <AttachmentsSection
                                data={currentContract.data.attachments}
                                onChange={(newData) => updateContractData({ ...currentContract.data, attachments: newData })}
                                contractData={currentContract.data}
                            />
                        </div>


                        <div className="section-block">
                            {(() => {
                                const signaturesData = {
                                    landlord: {
                                        fullName: currentContract.data.signatures?.landlord?.fullName || currentContract.data.parties?.landlordName || '',
                                        address: currentContract.data.signatures?.landlord?.address || '',
                                        passportSerial: currentContract.data.signatures?.landlord?.passportSerial || currentContract.data.parties?.landlordPassportSerial || '',
                                        passportNumber: currentContract.data.signatures?.landlord?.passportNumber || currentContract.data.parties?.landlordPassportNumber || '',
                                        passportIssuedBy: currentContract.data.signatures?.landlord?.passportIssuedBy || '',
                                        passportIssueDate: currentContract.data.signatures?.landlord?.passportIssueDate || '',
                                        passportDeptCode: currentContract.data.signatures?.landlord?.passportDeptCode || '',
                                        phone: currentContract.data.signatures?.landlord?.phone || '',
                                        email: currentContract.data.signatures?.landlord?.email || '',
                                        paymentDetails: currentContract.data.signatures?.landlord?.paymentDetails || ''
                                    },
                                    tenant: {
                                        fullName: currentContract.data.signatures?.tenant?.fullName || currentContract.data.parties?.tenantName || '',
                                        address: currentContract.data.signatures?.tenant?.address || '',
                                        passportSerial: currentContract.data.signatures?.tenant?.passportSerial || currentContract.data.parties?.tenantPassportSerial || '',
                                        passportNumber: currentContract.data.signatures?.tenant?.passportNumber || currentContract.data.parties?.tenantPassportNumber || '',
                                        passportIssuedBy: currentContract.data.signatures?.tenant?.passportIssuedBy || '',
                                        passportIssueDate: currentContract.data.signatures?.tenant?.passportIssueDate || '',
                                        passportDeptCode: currentContract.data.signatures?.tenant?.passportDeptCode || '',
                                        phone: currentContract.data.signatures?.tenant?.phone || '',
                                        email: currentContract.data.signatures?.tenant?.email || '',
                                        paymentDetails: currentContract.data.signatures?.tenant?.paymentDetails || ''
                                    }
                                };
                                return (
                                    <SignaturesSection
                                        data={signaturesData}
                                        onChange={(newData) => {
                                            // Обновляем signatures и синхронизируем parties, если они переданы
                                            const updatedData = { 
                                                ...currentContract.data, 
                                                signatures: {
                                                    landlord: newData.landlord,
                                                    tenant: newData.tenant,
                                                    moneyTransferAct: newData.moneyTransferAct || signaturesData.moneyTransferAct
                                                }
                                            };
                                            if (newData.parties) {
                                                updatedData.parties = { ...currentContract.data.parties, ...newData.parties };
                                            }
                                            updateContractData(updatedData);
                                        }}
                                        showValidation={showValidation}
                                        fullData={currentContract.data}
                                    />
                                );
                            })()}
                        </div>

                        {/* Акт приема-передачи жилого помещения (Приложение № 1) */}
                        {currentContract.data.attachments?.handoverAct?.enabled && (
                            <div className="section-block">
                                <HandoverActSection 
                                    contractData={currentContract.data} 
                                    onChange={(newData) => updateContractData(newData)}
                                    showValidation={showValidation}
                                />
                            </div>
                        )}

                        {/* Акт приема-передачи денежных средств - показывается только при наличных платежах (динамический номер: 2, если включен) */}
                        {currentContract?.data?.payment?.paymentMethod?.type === 'cash' && (
                            <div className="section-block">
                                <MoneyTransferActSection 
                                    contractData={currentContract.data} 
                                    onChange={(newData) => updateContractData(newData)}
                                />
                            </div>
                        )}

                        {/* Акт возврата жилого помещения (динамический номер: 2, если наличный расчет не выбран, иначе 3) */}
                        {currentContract.data.attachments?.returnAct?.enabled && (
                            <div className="section-block">
                                <ReturnActSection
                                    contractData={currentContract.data}
                                    onChange={(newData) => updateContractData(newData)}
                                    showValidation={showValidation}
                                />
                            </div>
                        )}
                    
                        <div className="section-block download-section">
                            <h2>Готовый документ</h2>
                            <p>Все обязательные поля заполнены.</p>
                            <div className="download-buttons">
                                <button onClick={() => handleDownload('word')} className="nav-button nav-button-primary" disabled={isSaving || downloadingFormat !== null}>
                                    {downloadingFormat === 'word' && <span className="btn-spinner" aria-hidden />}
                                    {isSaving ? 'Сохранение...' : downloadingFormat === 'word' ? 'Скачивание...' : 'Скачать Word'}
                                </button>
                                <button onClick={() => handleDownload('pdf')} className="nav-button nav-button-secondary" disabled={isSaving || downloadingFormat !== null} style={{ backgroundColor: 'var(--bg-subtle)' }}>
                                    {downloadingFormat === 'pdf' && <span className="btn-spinner btn-spinner-dark" aria-hidden />}
                                    {isSaving ? 'Сохранение...' : downloadingFormat === 'pdf' ? 'Скачивание...' : 'Скачать PDF'}
                                </button>
                            </div>
                        </div>
                    </main>
                    <aside className="wizard-sidebar">
                        <Sidebar />
                    </aside>
                </div>
            </div>
            <PaymentModal
                isOpen={paymentModal.isOpen}
                templateId={paymentModal.templateId}
                contractId={paymentModal.contractId}
                onClose={() => setPaymentModal({ isOpen: false, templateId: null, contractId: null })}
                onSuccess={() => {
                    setPaymentModal({ isOpen: false, templateId: null, contractId: null });
                    alert('Оплата прошла успешно! Теперь вы можете скачать документ.');
                }}
            />
            
            {/* ✨ Модалка валидации */}
            <InfoModal
                isOpen={validationModal}
                onClose={() => {
                    setValidationModal(false);
                    setValidationErrors([]);
                }}
                title="Заполните обязательные поля"
                closeOnlyByButton={true}
            >
                <div style={{ lineHeight: '1.6', marginBottom: '16px' }}>
                    {validationErrors.length > 0 && (
                        <div style={{ 
                            padding: '12px',
                            backgroundColor: '#fff5f5',
                            borderRadius: '4px',
                            border: '1px solid #fed7d7'
                        }}>
                            <p style={{ 
                                margin: 0,
                                color: '#c53030',
                                fontWeight: '500'
                            }}>
                                {validationErrors[0].message}
                            </p>
                        </div>
                    )}
                </div>
                <button 
                    onClick={() => {
                        setValidationModal(false);
                        setValidationErrors([]);
                    }} 
                    className="nav-button nav-button-primary"
                    style={{ width: '100%' }}
                >
                    Понятно
                </button>
            </InfoModal>
        </>
    );
};

export default ContractWizard;